/**
 * Login page — JWT username+password authentication.
 *
 * Single-step: username + password → POST /auth/login → JWT + user + org
 * Store JWT → redirect to settings.
 *
 * No magic-link. No public registration. Users via seed/admin script only.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import { API_V2_URL } from '@/lib/api';
import { authErrorMessage } from '@/lib/auth-errors';
import { TurnstileWidget, type TurnstileWidgetHandle } from '@/components/TurnstileWidget';
import { getTurnstileSiteKey } from '@/lib/turnstile';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Turnstile (P27) — site key absent (default local dev) = widget not
  // rendered, no token sent, flow unchanged.
  const turnstileSiteKey = getTurnstileSiteKey();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  useEffect(() => {
    document.title = 'Sign In - 11to1';
  }, []);

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/settings' });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Raw fetch by design: pre-session bootstrap (CSRF-exempt public route),
      // and a 401 here means bad credentials — it must not trigger the
      // apiV2Fetch wrapper's global auth:logout dispatch.
      const res = await fetch(`${API_V2_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: username.trim(),
          password,
          ...(turnstileToken ? { turnstileToken } : {}),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        // Turnstile tokens are single-use — get a fresh one for the retry.
        if (turnstileSiteKey) {
          setTurnstileToken(null);
          turnstileRef.current?.reset();
        }
        // Never surface a raw machine code (LOGIN_LOCKED, RATE_LIMITED, …) —
        // prefer the backend's sanitized message, else map known codes.
        throw new Error(authErrorMessage(data, 'Invalid credentials'));
      }

      // Cookie-session login: backend sets session cookie; token is no longer returned.
      login(data.token ?? null, data.user, data.org);
      navigate({ to: '/settings' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text">11to1</h1>
          <p className="text-muted mt-2">Sign in to your workspace</p>
        </div>

        {/* Login Card */}
        <div className="card">
          <form onSubmit={handleSubmit}>
            <h2 className="text-xl font-semibold text-text mb-4 text-center">Welcome</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-text mb-1">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="w-full px-3 py-2 border border-border rounded-md bg-bg text-text focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  autoFocus
                  autoComplete="username"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border border-border rounded-md bg-bg text-text focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {turnstileSiteKey && (
              <div className="mt-4 flex justify-center">
                <TurnstileWidget
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  onToken={setTurnstileToken}
                />
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 rounded-md bg-danger/10 border border-danger/20">
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username.trim() || !password || (!!turnstileSiteKey && !turnstileToken)}
              className="btn-primary w-full py-3 text-base mt-6"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
